import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSession } from "../lib/session";
import { getCompetition, getSwindle, enableSwindle, disableSwindle,
         listPrizes, savePrize, deletePrize, addPrizes } from "../lib/api";
import { PageHead, Loading, Notice, Field } from "../components/ui";
import Back from "../components/Back";
import PrizePicker from "../components/PrizePicker";

/**
 * Setting the swindle up: the buy-in, and every prize in it.
 *
 * The starting list is what this society has always played, but none of
 * it is fixed — a prize is which round, which holes, which metric and how
 * much, and all four are editable.
 */

const KINDS = [
  ["stableford",  "Most Stableford points"],
  ["nett",        "Fewest nett strokes"],
  ["gross",       "Fewest strokes"],
  ["birdie_rate", "Rate per birdie or nett eagle"],
];
const HOLES = [
  ["all", "All 18"], ["front9", "Front nine"], ["back9", "Back nine"],
  ["par3", "The par 3s"], ["par4", "The par 4s"], ["par5", "The par 5s"],
];

const blank = (competition_id) => ({
  competition_id, round_id: null, label: "", kind: "stableford",
  holes: "all", amount: 0, ace_takes_pot: false,
});

export default function SwindleSettings() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isOrganiser } = useSession();

  const [comp, setComp] = useState(null);
  const [swindle, setSwindle] = useState(null);
  const [prizes, setPrizes] = useState([]);
  const [buyIn, setBuyIn] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [ready, setReady] = useState(false);

  const load = async () => {
    try {
      const [c, s] = await Promise.all([getCompetition(id), getSwindle(id)]);
      const p = s ? await listPrizes(id) : [];
      // all at once, so the buy-in field never shows a placeholder figure
      setComp(c); setSwindle(s); setBuyIn(s?.buy_in ?? 10); setPrizes(p);
      setReady(true);
    } catch (e) { setError(e.message); setReady(true); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!isOrganiser) return <Notice kind="error">Only an organiser can change the swindle.</Notice>;
  if (!ready) return <Loading />;
  if (!comp) return (
    <>
      <Back to="/competitions">Back</Back>
      <Notice kind="error">That competition isn't available.</Notice>
    </>
  );

  const rounds = (comp.rounds ?? []).sort((a, b) => a.round_number - b.round_number);

  // same grouping as the swindle page: by round, competition-wide last
  const groups = [
    ...rounds
      .map((r) => ({ key: r.id, title: r.name,
                     prizes: prizes.filter((p) => p.round_id === r.id) }))
      .filter((g) => g.prizes.length > 0),
    ...(prizes.some((p) => !p.round_id)
      ? [{ key: "all", title: "Across the competition",
           prizes: prizes.filter((p) => !p.round_id) }]
      : []),
  ];
  const run = async (fn, msg) => {
    setError(""); setDone(""); setBusy(true);
    try {
      await fn();          // may set its own message
      await load();
      if (msg) setDone(msg);
    }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Back to={`/c/${id}/swindle`}>The swindle</Back>
      <PageHead title="Swindle settings" sub={comp.name} />

      <Notice kind="error">{error}</Notice>
      <Notice kind="ok">{done}</Notice>

      <form className="surface" style={{ padding: "1.1rem", marginBottom: "1.25rem", maxWidth: 460 }}
            onSubmit={(e) => { e.preventDefault();
              run(() => enableSwindle(id, buyIn), "Buy-in saved."); }}>
        <h2 style={{ fontSize: "1rem", marginBottom: ".8rem" }}>Buy-in</h2>
        <Field label="Amount per player"
               hint="What each player puts in. The pot is this times however many have paid.">
          <input className="input" inputMode="decimal" required value={buyIn}
                 onChange={(e) => setBuyIn(e.target.value)} />
        </Field>
        <button className="btn btn-primary" disabled={busy}>
          {busy ? "Saving…" : swindle ? "Save buy-in" : "Start the swindle"}
        </button>
      </form>

      {swindle && (
        <>
          <div style={{ marginBottom: "1.25rem" }}>
            <PrizePicker rounds={rounds} busy={busy}
                         onAdd={(specs, clear) =>
                           run(async () => {
                             const n = await addPrizes(id, specs);
                             clear();
                             setDone(n === 0
                               ? "Those prizes were already on the list."
                               : `Added ${n} prize${n === 1 ? "" : "s"}.`);
                           })} />
          </div>

          <div className="row" style={{ marginBottom: ".6rem" }}>
            <h2 style={{ fontSize: ".9375rem", fontWeight: 700 }}>
              Prizes · {prizes.length}
            </h2>
            <span className="spacer" />
            {!editing && (
              <button className="btn-link" onClick={() => setEditing(blank(id))}>
                Add a prize
              </button>
            )}
          </div>

          {editing && (
            <form className="surface" style={{ padding: "1.1rem", marginBottom: ".8rem" }}
                  onSubmit={(e) => { e.preventDefault();
                    run(async () => { await savePrize(editing); setEditing(null); }); }}>
              <Field label="What it's called">
                <input className="input" required autoFocus value={editing.label}
                       onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                       placeholder="Round 1 · front nine" />
              </Field>
              <div className="grid2">
                <Field label="Round">
                  <select className="select" value={editing.round_id ?? ""}
                          onChange={(e) => setEditing({ ...editing, round_id: e.target.value || null })}>
                    <option value="">Whole competition</option>
                    {rounds.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </Field>
                <Field label="Amount" hint={editing.kind === "birdie_rate" ? "Paid for each one." : undefined}>
                  <input className="input" inputMode="decimal" value={editing.amount}
                         onChange={(e) => setEditing({ ...editing, amount: e.target.value })} />
                </Field>
              </div>
              <div className="grid2">
                <Field label="Decided by">
                  <select className="select" value={editing.kind}
                          onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                    {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </Field>
                <Field label="Holes that count">
                  <select className="select" value={editing.holes}
                          onChange={(e) => setEditing({ ...editing, holes: e.target.value })}>
                    {HOLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </Field>
              </div>
              {editing.kind === "birdie_rate" && (
                <label className="choice" style={{ marginBottom: ".8rem" }}>
                  <input type="checkbox" checked={!!editing.ace_takes_pot}
                         onChange={(e) => setEditing({ ...editing, ace_takes_pot: e.target.checked })} />
                  <span>
                    <strong>A hole in one takes the pot</strong>
                    <small>
                      Whoever holes one collects everything this prize would have
                      paid that round, instead of everyone being paid per birdie.
                      Two aces split it.
                    </small>
                  </span>
                </label>
              )}

              <div className="row">
                <button className="btn btn-primary" disabled={busy || !editing.label.trim()}>
                  {busy ? "Saving…" : "Save prize"}
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                  Cancel
                </button>
                <span className="spacer" />
                {editing.id && (
                  <button type="button" className="btn-danger"
                          onClick={() => {
                            if (!window.confirm(`Remove "${editing.label}"?`)) return;
                            run(async () => { await deletePrize(editing.id); setEditing(null); });
                          }}>Remove</button>
                )}
              </div>
            </form>
          )}

          {prizes.length === 0 && (
            <p className="muted" style={{ fontSize: ".8125rem", lineHeight: 1.6 }}>
              No prizes yet. Tick some above, or add one by hand.
            </p>
          )}

          {groups.map((g) => (
            <div key={g.key} style={{ marginBottom: ".8rem" }}>
              <div className="row" style={{ marginBottom: ".35rem" }}>
                <h3 className="eyebrow">{g.title}</h3>
                <span className="spacer" />
                <span className="muted" style={{ fontSize: ".75rem" }}>
                  {g.prizes.reduce((t, p) => t + Number(p.amount || 0), 0).toFixed(2)}
                  {" · "}{g.prizes.length} prize{g.prizes.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="list">
                {g.prizes.map((p) => (
              <button className="list-row" key={p.id} onClick={() => setEditing(p)}>
                <span style={{ minWidth: 0 }}>
                  <strong style={{ display: "block", overflow: "hidden",
                                   textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.label.replace(`${g.title} · `, "")}
                  </strong>
                  <small className="muted" style={{ fontSize: ".6875rem" }}>
                    {KINDS.find(([k]) => k === p.kind)?.[1]} ·{" "}
                    {HOLES.find(([k]) => k === p.holes)?.[1]}
                  </small>
                </span>
                <span className="fig" style={{ textAlign: "right" }}>
                  {Number(p.amount).toFixed(2)}
                </span>
              </button>
                ))}
              </div>
            </div>
          ))}

          <div className="surface danger-zone" style={{ padding: "1.1rem", marginTop: "1.5rem",
                                                        maxWidth: 460 }}>
            <h2 style={{ fontSize: "1rem", marginBottom: ".4rem" }}>Remove the swindle</h2>
            <p className="muted" style={{ fontSize: ".8125rem", lineHeight: 1.6, margin: "0 0 .9rem" }}>
              Takes the swindle and every prize off this competition. Scores
              aren't touched — only the side bets.
            </p>
            <button className="btn-danger" disabled={busy}
                    onClick={() => {
                      if (!window.confirm("Remove the swindle and all its prizes?")) return;
                      run(async () => { await disableSwindle(id); nav(`/c/${id}/today`); });
                    }}>
              Remove the swindle
            </button>
          </div>
        </>
      )}
    </>
  );
}
